<template>
  <div class="map-view">
    <NavBar
      :onUserIconClick="onUserIconClick"
      :udSelected="udSelected"
      :toggleUdSelected="toggleUdSelected"
    />
    <div class="content">
      <NavTouch
        :mapViewSelected="mapViewSelected"
        :setMapViewSelected="setMapViewSelected"
      />
      <itinerary-view
        :itinerary="itinerary"
        :fromPlace="fromPlace"
        :toPlace="toPlace"
        :loadingItinerary="state.loadingItinerary"
      />
      <div
        id="trip-view-map"
        :style="{ zIndex: mapViewSelected ? '3' :  '1' }"
      >
        <a
          id="septimalogo"
          href="https://www.septima.dk"
        >
          <SeptimaLogo
            :height="18"
            :iconColor="'rgba(0,0,0,0.5)'"
          />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import NavBar from '@/components/NavBar/NavBar';
import NavTouch from '@/components/NavTouch/NavTouch';
import ItineraryView from '@/components/Itinerary/ItineraryView';
import SeptimaLogo from '@/components/Icons/SeptimaLogo.vue';
import { createMap, setMapItinerary, showStep } from '@/mapUtil';
import { loadLayers } from '@/mapUtil/layers';
import { tripType } from '@/util/otp';
import {
  loadTrip,
  toggleUdSelected,
  getMarker,
  selectItinerary,
  onUserIconClick,
  getItineraries,
  setFromPlace,
  setToPlace,
  setMapViewSelected,
  resetItinerary,
  clearItineraries,
} from '@/util/itinerary';
export default {
  name: 'trip-view',
  components: { NavBar, ItineraryView, NavTouch, SeptimaLogo },
  mounted() {
    this.map = createMap('trip-view-map');
    this.map.on('load', () => {
      loadLayers(this.map);
      this.map.addControl(
        new Mapbox.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
        }),
        'top-left'
      );
      this.map.addControl(
        new Mapbox.ScaleControl({
          maxWidth: 80,
          unit: 'metric',
        }),
        'bottom-right'
      );
      this.map.addControl(new Mapbox.NavigationControl());
      const { id } = this.$route.params;
      if (id) {
        this.loadTrip(id);
      }
    });
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'setSelectedItineraryIndex') {
        const { selectedItineraryIndex } = mutation.payload;
        this.setMapItinerary(
          this.map,
          state.itineraries[selectedItineraryIndex],
          selectedItineraryIndex
        );
        this.itinerary = state.itineraries[selectedItineraryIndex];
      }
      if (mutation.type === 'stepClicked') {
        showStep(this.map, mutation.payload);
        this.mapViewSelected = true;
      }
      if (mutation.type === 'setToPlace') {
        this.getItineraries();
      }
    });
  },
  beforeDestroy() {
    this.udSelected = true;
    this.resetItinerary();
    this.map.remove();
    this.unsubscribe();
  },
  data: function() {
    return {
      setMapItinerary: setMapItinerary.bind(this),
      mapViewSelected: false,
      resetItinerary: resetItinerary.bind(this),
      clearItineraries: clearItineraries.bind(this),
      setMapViewSelected: setMapViewSelected.bind(this),
      loadTrip: loadTrip.bind(this),
      selectItinerary: selectItinerary.bind(this),
      getMarker,
      toggleUdSelected: toggleUdSelected.bind(this),
      onUserIconClick: onUserIconClick.bind(this),
      getItineraries: getItineraries.bind(this),
      setFromPlace: setFromPlace.bind(this),
      setToPlace: setToPlace.bind(this),
      map: null,
      itinerary: undefined,
      udSelected: true,
      state: this.$store.state,
      fromPlaceMarker: undefined,
      toPlaceMarker: undefined,
      unsubscribe: undefined,
    };
  },
  computed: {
    tripType: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud
            ? this.state.trip.ud.tripType
            : tripType.standard;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.tripType
          : tripType.standard;
      },
    },
    viaPlace: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.ud ? this.ud.viaPlace : undefined;
        }
        return this.retur ? this.retur.viaPlace : undefined;
      },
    },
    startTime: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud.startTime;
        }
        if (this.state.trip.retur) {
          return this.state.trip.retur.startTime;
        }
        return undefined;
      },
    },
    fromPlace: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud ? this.state.trip.ud.fromPlace : undefined;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.fromPlace
          : undefined;
      },
    },
    toPlace: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud ? this.state.trip.ud.toPlace : undefined;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.toPlace
          : undefined;
      },
    },
    selectedItineraryIndex: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud
            ? this.state.trip.ud.selectedItineraryIndex
            : 0;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.selectedItineraryIndex
          : 0;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.map-view {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: $primary-700;
}
.content {
  flex: 1;
  display: flex;
  overflow: hidden;
}
#trip-view-map {
  width: 100%;
  height: 100%;
  flex-grow: 1;
  flex-basis: 0;
  min-height: 0;
  margin-left: 2px;
  background-color: #eff1f9;
}
@media only screen and (max-width: $tablet-landscape-max-width) {
  .content {
    display: block;
    position: relative;
    margin-right: 90px;
  }
  #trip-view-map {
    position: absolute;
    top: 0;
    left: 0;
    margin-left: 0;
  }
}

@media only screen and (max-width: $mobile-max-width) {
  .content {
    margin-right: 0;
    margin-bottom: 70px;
  }

  .map-view {
    background-color: transparent;
  }
}
</style>
